<template>
  <l-default
    hide-favorites-basket
    :class="b()"
  >
    <c-container>
      <c-breadcrumb :class="b('breadcrumb')" />
      <div :class="b('heading')">
        <h4 :class="b('title')">{{ $t('l-favorites-overview.title') }}</h4>
        <div
          v-if="showNav"
          :class="b('nav')"
        >
          <e-button
            v-for="(action, index) in actions"
            :key="index"
            :class="b('action-btn')"
            @click="action.callback"
          >
            <span>{{ action.label }}</span>
          </e-button>
        </div>
        <div
          v-if="isPreparingData"
          :class="b('loading-spinner')"
        >
          <e-progress />
        </div>
      </div>
      <div
        v-if="showClearListConfirmation"
        :class="b('clear-list-confirmation')"
      >
        <div :class="b('confirmation-message')">{{ $t('l-favorites-overview.clear-list-confirmation-message') }}</div>
        <div :class="b('clear-list-actions')">
          <e-button
            :class="b('clear-list-action')"
            @click="handleClearList"
            >{{ $t('l-favorites-overview.yes') }}</e-button
          >
          <e-button
            :class="b('clear-list-action')"
            @click="showClearListConfirmation = !showClearListConfirmation"
            >{{ $t('l-favorites-overview.no') }}</e-button
          >
        </div>
      </div>
      <e-progress
        v-if="isFirstFetch"
        :class="b('loading-indicator')"
      />
      <ul v-if="favoritesStore.favorites?.length">
        <li
          v-for="item in favoritesStore.favorites"
          :key="item.id"
          :class="b('favorite-product-wrapper')"
        >
          <a
            :class="b('favorite-product')"
            :href="item.url"
          >
            <e-picture
              v-if="item.mainImage"
              :srcset="item.mainImage.srcset"
              :alt="item.mainImage?.alt || item.mainImage?.title || $t('c-product-image.alt-text')"
              :title="item.mainImage.title"
              :fallback="item.mainImage.fallback"
              :ratio="1"
              :placeholder="!item.mainImage"
              loading="lazy"
              :class="b('image')"
            />
            <div :class="b('info')">
              <h3 :class="b('name')">
                {{ item.name }}
              </h3>
              <p :class="b('reference-code')">
                {{ $t('l-favorites-overview.referenceCode', { sku: item.sku }) }}
              </p>
            </div>

            <e-button
              :class="b('remove-btn')"
              @click.prevent="favoritesStore.removeFavorite(item)"
            >
              <e-icon
                icon="i-close"
                size="24"
              />
            </e-button>
          </a>
        </li>
      </ul>
      <p
        v-if="!isFirstFetch && !favoritesStore.favorites?.length"
        :class="b('no-favorites-found')"
      >
        {{ $t('l-favorites-overview.no-favorites-found') }}
      </p>
    </c-container>
  </l-default>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import useFavoritesStore from '@/stores/favorites';
  import useNotificationStore from '@/stores/notification';
  import cBreadcrumb from '@/components/c-breadcrumb.vue';
  import cContainer from '@/components/c-container.vue';
  import eButton from '@/elements/e-button.vue';
  import eIcon from '@/elements/e-icon.vue';
  import ePicture from '@/elements/e-picture.vue';
  import eProgress from '@/elements/e-progress.vue';
  import lDefault from '@/layouts/l-default.vue';

  type FavoritesAction = {
    label: string;
    callback(): void;
  };

  type Setup = {
    favoritesStore: ReturnType<typeof useFavoritesStore>;
    notificationStore: ReturnType<typeof useNotificationStore>;
  };

  type Data = {
    showClearListConfirmation: boolean;
  };

  /**
   * Renders an article list page.
   */
  export default defineComponent({
    name: 'l-favorites-overview',

    components: {
      cBreadcrumb,
      cContainer,
      lDefault,
      ePicture,
      eIcon,
      eButton,
      eProgress,
    },

    // props: {},
    // emits: {},

    setup(): Setup {
      return {
        favoritesStore: useFavoritesStore(),
        notificationStore: useNotificationStore(),
      };
    },
    data(): Data {
      return {
        showClearListConfirmation: false,
      };
    },

    computed: {
      isFirstFetch(): boolean {
        return this.favoritesStore.runningRequests.apiFetchFavorites && this.favoritesStore.favorites === null;
      },

      isPreparingData(): boolean {
        return (
          this.favoritesStore.runningRequests.apiExportList ||
          this.favoritesStore.runningRequests.apiExportTendertexts ||
          this.favoritesStore.runningRequests.apiExportDatasheets
        );
      },

      showNav(): boolean {
        return !!this.favoritesStore.favorites?.length && !this.isPreparingData;
      },

      actions(): FavoritesAction[] {
        return [
          {
            label: this.$t('l-favorites-overview.clearList'),
            callback: this.promptClearListConfirmation,
          },
          {
            label: this.$t('l-favorites-overview.exportList'),
            callback: () =>
              this.handleExport(() => this.favoritesStore.exportList(this.$t('favorites.list-export-filename-prefix'))),
          },
          {
            label: this.$t('l-favorites-overview.exportTendertexts'),
            callback: () =>
              this.handleExport(() =>
                this.favoritesStore.exportTendertexts(this.$t('favorites.tendertext-export-filename-prefix')),
              ),
          },
          {
            label: this.$t('l-favorites-overview.exportDatasheets'),
            callback: () =>
              this.handleExport(() =>
                this.favoritesStore.exportDatasheets(this.$t('favorites.datasheet-export-filename-prefix')),
              ),
          },
        ];
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.favoritesStore.apiFetchFavorites();
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      promptClearListConfirmation(): void {
        this.showClearListConfirmation = true;
      },

      handleExport(exportHandler: () => void): void {
        this.showClearListConfirmation = false;
        this.notificationStore.showNotification({
          type: 'info',
          message: this.$t('l-favorites-overview.loading-notice'),
          expire: true,
          secondsToShowInMs: 10000,
        });
        exportHandler();
      },

      handleClearList(): void {
        this.favoritesStore.clearList();
        this.showClearListConfirmation = false;
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .l-favorites-overview {
    padding: 3rem 0;

    &__breadcrumb {
      margin-bottom: 3rem;
    }

    &__heading {
      width: 100%;
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      justify-content: space-between;
      gap: 1rem;
      background-color: var(--theme-color-primary--1);
      color: var(--theme-color-grayscale--950);
      padding: 1.5rem;

      @include mixins.media(sm) {
        align-items: center;
        flex-direction: row;
        gap: 2rem;
      }
    }

    &__title {
      font-size: 1.125rem;
      font-weight: 700;
    }

    &__nav {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin-top: 1rem;

      @include mixins.media(sm) {
        flex-direction: row;
        margin-top: 0;
      }
    }

    &__action-btn {
      color: var(--theme-color-grayscale--950);

      @include mixins.media(sm) {
        padding: 0 0.75rem;
        border-right: 1px solid var(--theme-color-grayscale--950);
      }

      &:last-child {
        border: none;
      }
    }

    &__loading-spinner {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;

      @include mixins.media(sm) {
        width: 50%;
      }

      .e-progress__bubble {
        background-color: var(--theme-color-grayscale--1000);
      }
    }

    &__clear-list-confirmation {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin: 0.75rem 0;
      padding: 0.75rem 1.5rem;
      background-color: var(--theme-color-grayscale--950);
      border: 1px solid var(--theme-color-grayscale--800);
      border-radius: 0.25rem;

      @include mixins.media(sm) {
        flex-direction: row;
        align-items: center;
      }
    }

    &__confirmation-message {
      margin-bottom: 1.5rem;

      @include mixins.media(sm) {
        margin-bottom: 0;
      }
    }

    &__clear-list-actions {
      display: flex;
      flex-direction: row;
      gap: 0.5rem;
    }

    &__clear-list-action {
      line-height: 2.85rem;
      padding: 0 1.5rem;
      background-color: var(--theme-color-primary--1);
      color: var(--theme-color-grayscale--950);
      border-radius: 1.5rem;
    }

    &__loading-indicator {
      padding: 1rem;
      align-self: center;
    }

    &__favorite-product-wrapper {
      border-bottom: 1px solid var(--theme-color-grayscale--800);

      &:last-child {
        border: none;
      }
    }

    &__favorite-product {
      @include mixins.hover-effect--default;

      display: grid;
      grid-template-columns: 1fr 30px;
      grid-template-areas:
        'image button'
        'info info';
      gap: 1.5rem;
      padding: 1.5rem 0;
      width: 100%;

      @include mixins.media(sm) {
        grid-template-columns: 9.5rem 1fr 30px;
        grid-template-areas: 'image info button';
      }
    }

    &__image {
      grid-area: image;
      min-width: 9.5rem;
      max-width: 9.5rem;
    }

    &__info {
      grid-area: info;
    }

    &__name {
      /* stylelint-disable order/order */
      font-weight: 400;

      @include mixins.heading-h3;
    }

    &__reference-code {
      margin-top: 0.5rem;
    }

    &__remove-btn {
      grid-area: button;
      place-self: start end;
    }

    &__no-favorites-found {
      font-style: italic;
      padding: 1rem;
    }
  }
</style>
